import XMarkIcon from '@heroicons/react/24/solid/XMarkIcon';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type BottomSheetProps = {
  children: React.ReactNode;
  open: boolean;
  onClose: () => void;
  id?: string;
  title?: string;
};

export function BottomSheet({
  children,
  id,
  open,
  onClose,
  title,
}: BottomSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent) => {
      if (overlayRef.current && e.target === overlayRef.current) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            ref={overlayRef}
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black z-40"
            aria-modal="true"
            role="dialog"
            style={{ touchAction: 'none' }}
          />
          <motion.div
            key="sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.35,
            }}
            className="fixed left-0 right-0 bottom-0 mx-auto max-w-md w-full bg-white rounded-t-2xl pt-6 pb-2 shadow-lg z-50 flex flex-col overflow-y-auto"
            style={{
              maxHeight: '60vh',
              overscrollBehaviorY: 'contain',
            }}
          >
            <div className="flex items-center justify-between pb-4 px-4 border-b">
              {title && <h5 className="text-lg font-semibold">{title}</h5>}
              <button
                onClick={onClose}
                aria-label="Fechar"
                tabIndex={0}
                type="button"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div
              className="pt-4 px-4 overflow-y-auto"
              style={{ touchAction: 'auto' }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
    id,
  );
}
